import React from 'react';
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import CheckIcon from "@mui/icons-material/Check";

// Bell notification icon
const BellIcon = () => (
    <svg 
        width="32" 
        height="32" 
        viewBox="0 0 24 24" 
        fill="none" 
        xmlns="http://www.w3.org/2000/svg"
    >
        <path 
            d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" 
            stroke="#1A1A1A" 
            strokeWidth="1.5" 
            strokeLinecap="round" 
            strokeLinejoin="round"
            fill="none"
        />
        <path 
            d="M13.73 21a2 2 0 0 1-3.46 0" 
            stroke="#1A1A1A" 
            strokeWidth="1.5" 
            strokeLinecap="round" 
            strokeLinejoin="round"
            fill="none"
        />
    </svg>
);

/**
 * Single pending guest row with approve/deny buttons
 */
const PendingGuestRow = ({ guest, onApprove, onDeny, isProcessing }) => {
    const getAvatarColor = (name) => {
        // Generate a consistent color based on name
        const colors = ['#E5D9C8', '#D4E5D7', '#D9E5E5', '#E5D9E5', '#E5E0D4'];
        const index = name?.charCodeAt(0) % colors.length || 0;
        return colors[index];
    };

    return (
        <Box sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            p: 2,
            backgroundColor: '#FFFFFF',
            borderRadius: '12px',
            border: '1px solid #F3F4F6',
            boxShadow: '0 1px 2px rgba(0,0,0,0.04)',
        }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                {/* Avatar */}
                <Box sx={{
                    width: 36,
                    height: 36,
                    borderRadius: '50%',
                    backgroundColor: getAvatarColor(guest.guestName),
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                }}>
                    <Typography sx={{
                        fontFamily: 'Inter, sans-serif',
                        fontSize: '14px',
                        fontWeight: 600,
                        color: '#1A1A1A',
                    }}>
                        {guest.guestName?.charAt(0)?.toUpperCase() || 'G'}
                    </Typography>
                </Box>
                
                {/* Name */}
                <Box>
                    <Typography sx={{
                        fontFamily: 'Inter, sans-serif',
                        fontSize: '14px',
                        fontWeight: 500,
                        color: '#1A1A1A',
                    }}>
                        {guest.guestName}
                    </Typography>
                </Box>
            </Box>

            {/* Action buttons */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {/* Approve button */}
                <IconButton
                    onClick={() => onApprove(guest)}
                    disabled={isProcessing}
                    sx={{
                        width: 32,
                        height: 32,
                        backgroundColor: 'transparent',
                        color: '#22C55E',
                        '&:hover': {
                            backgroundColor: 'rgba(34, 197, 94, 0.1)',
                        },
                        '&:disabled': {
                            opacity: 0.5,
                        },
                    }}
                >
                    <CheckIcon sx={{ fontSize: 20 }} />
                </IconButton>
                
                {/* Deny button */}
                <IconButton
                    onClick={() => onDeny(guest)}
                    disabled={isProcessing}
                    sx={{
                        width: 32,
                        height: 32,
                        backgroundColor: 'transparent',
                        color: '#EF4444',
                        '&:hover': {
                            backgroundColor: 'rgba(239, 68, 68, 0.1)',
                        },
                        '&:disabled': {
                            opacity: 0.5,
                        },
                    }}
                >
                    <CloseIcon sx={{ fontSize: 20 }} />
                </IconButton>
            </Box>
        </Box>
    );
};

/**
 * GuestAdmissionModal - Overlay/modal shown to host when guests are waiting
 * Shows list of pending guests with approve/deny buttons for each
 * Positioned in top-right corner near the invite button
 */
const GuestAdmissionModal = ({ 
    pendingGuests = [], 
    onApprove, 
    onDeny, 
    onClose,
    isProcessing = false,
}) => {
    if (pendingGuests.length === 0) {
        return null;
    }

    return (
        <Box sx={{
            position: 'fixed',
            top: 60,
            right: 100,
            zIndex: 1200,
            width: 340,
        }}>
            <Box sx={{
                backgroundColor: '#FFFFFF',
                borderRadius: '16px',
                boxShadow: '0 8px 32px rgba(0,0,0,0.12), 0 0 0 1px rgba(0,0,0,0.04)',
                overflow: 'hidden',
            }}>
                {/* Header */}
                <Box sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    px: 3,
                    py: 2.5,
                    borderBottom: '1px solid #F3F4F6',
                }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <BellIcon />
                        <Box>
                            <Typography sx={{
                                fontFamily: 'Inter, sans-serif',
                                fontSize: '14px',
                                fontWeight: 400,
                                color: '#6B7280',
                            }}>
                                {pendingGuests.length === 1 
                                    ? 'Someone would like to join the Room'
                                    : `${pendingGuests.length} people would like to join`
                                }
                            </Typography>
                        </Box>
                    </Box>
                    
                    {/* Close button */}
                    <IconButton
                        onClick={onClose}
                        sx={{
                            width: 28,
                            height: 28,
                            color: '#9CA3AF',
                            '&:hover': {
                                backgroundColor: '#F3F4F6',
                                color: '#6B7280',
                            },
                        }}
                    >
                        <CloseIcon sx={{ fontSize: 18 }} />
                    </IconButton>
                </Box>

                {/* Guest list */}
                <Box sx={{
                    p: 2,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 1.5,
                    maxHeight: 300,
                    overflowY: 'auto',
                }}>
                    {pendingGuests.map((guest) => (
                        <PendingGuestRow
                            key={guest.pendingId}
                            guest={guest}
                            onApprove={onApprove}
                            onDeny={onDeny}
                            isProcessing={isProcessing}
                        />
                    ))}
                </Box>
            </Box>
        </Box>
    );
};

export default GuestAdmissionModal;

